import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Play, Share2, Calendar, FileType, HardDrive } from 'lucide-react';
import { motion } from 'framer-motion';
import { useMedia } from '@/contexts/MediaContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';

const MediaDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { getFileById } = useMedia();
  const [media, setMedia] = useState(location.state?.item || {});
  const [loading, setLoading] = useState(!location.state?.item);

  useEffect(() => {
    if (!location.state?.item && id) {
      const loadMedia = async () => {
        try {
          setLoading(true);
          const data = await getFileById(id);
          if (data) {
            setMedia(data);
          } else {
            toast({
              title: 'Erro',
              description: 'Mídia não encontrada.',
              variant: 'destructive',
            });
            navigate('/');
          }
        } catch (error) {
          console.error('Erro ao carregar mídia:', error);
          toast({
            title: 'Erro',
            description: 'Não foi possível carregar os detalhes da mídia.',
            variant: 'destructive',
          });
        } finally {
          setLoading(false);
        }
      };
      loadMedia();
    }
  }, [id, location.state, toast, navigate, getFileById]);

  const handleDownload = () => {
    const url = media?.url || media?.google_drive_url || '#';
    if (url && url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast({
        title: 'Erro',
        description: 'Link de download não disponível.',
        variant: 'destructive',
      });
    }
  };

  const handleShare = async () => {
    if (!media?.id) return;
    try {
      const shareUrl = `${window.location.origin}/share/${media.id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Link copiado!',
        description: 'Link copiado para a área de transferência.',
      });
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o link.',
        variant: 'destructive',
      });
    }
  };

  const handlePlay = () => {
    if (media?.type?.startsWith('video') || media?.type?.startsWith('audio')) {
      const streamUrl = media?.url || media?.google_drive_url;
      if (streamUrl) {
        window.open(streamUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      handleDownload();
    }
  };

  const formatSize = (size) => {
    if (!size) return '—';
    const units = ['B', 'KB', 'MB', 'GB'];
    let s = size;
    let i = 0;
    while (s >= 1024 && i < units.length - 1) {
      s /= 1024;
      i += 1;
    }
    return `${s.toFixed(2)} ${units[i]}`;
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-full pb-8"
    >
      <div className="max-w-6xl mx-auto space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-950/30 transition-all pl-0 hover:pl-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Button>

        <div className="rounded-3xl bg-slate-900/50 backdrop-blur-xl border border-slate-800 overflow-hidden shadow-2xl shadow-cyan-500/10">
          {/* Header */}
          <div className="p-8 border-b border-slate-800 bg-slate-900/50">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
              <div className="flex-1">
                <p className="text-xs text-slate-500 font-mono mb-2">ID: {id}</p>
                <h1 className="text-3xl md:text-4xl font-black text-white mb-4 tracking-tight">
                  {media.title || media.name || 'Mídia'}
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-full text-sm font-medium capitalize">
                    <FileType className="h-3.5 w-3.5" />
                    {media.type || 'tipo não informado'}
                  </span>
                  {media.date || media.createdAt ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-800 text-slate-400 rounded-full text-sm">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(media.date || media.createdAt).toLocaleDateString('pt-BR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartilhar
                </Button>
                {(media.type?.startsWith('video') || media.type?.startsWith('audio')) ? (
                  <Button
                    onClick={handlePlay}
                    className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white shadow-lg shadow-cyan-500/20 border-0"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Reproduzir
                  </Button>
                ) : (
                  <Button
                    onClick={handleDownload}
                    className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white shadow-lg shadow-cyan-500/20 border-0"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="aspect-video bg-slate-950/50 flex items-center justify-center overflow-hidden relative group">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-slate-900/0 to-slate-950 pointer-events-none" />
            {media.thumbnail || media.thumbnailUrl || media.url ? (
              media.type?.startsWith('video') ? (
                 <video 
                    src={media.url} 
                    className="w-full h-full object-contain max-h-[60vh]" 
                    controls
                    poster={media.thumbnail || media.thumbnailUrl}
                 />
              ) : media.type?.startsWith('image') ? (
                <img
                  src={media.url || media.thumbnail || media.thumbnailUrl}
                  alt={media.title || 'Mídia'}
                  className="w-full h-full object-contain max-h-[60vh] transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                 <div className="text-center p-12">
                   <FileType className="h-24 w-24 text-slate-700 mx-auto mb-4" />
                   <p className="text-slate-500">Visualização não disponível para este tipo de arquivo</p>
                 </div>
              )
            ) : (
              <div className="text-center p-12">
                <FileType className="h-24 w-24 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500">Sem visualização</p>
              </div>
            )}
          </div>

          {/* Info Grid */}
          <div className="p-8 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-slate-800 bg-slate-900/30">
            <div className="space-y-2">
              <p className="text-slate-500 text-xs uppercase font-bold tracking-wider">Tipo</p>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]"></span>
                <p className="text-slate-200 font-medium capitalize">{media.type || '—'}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <p className="text-slate-500 text-xs uppercase font-bold tracking-wider">Tamanho</p>
              <div className="flex items-center gap-2 text-slate-200 font-medium">
                <HardDrive className="h-4 w-4 text-slate-400" />
                {formatSize(media.size)}
              </div>
            </div>
            
            <div className="space-y-2">
              <p className="text-slate-500 text-xs uppercase font-bold tracking-wider">Categoria</p>
              <p className="text-slate-200 font-medium capitalize">{media.category || 'Geral'}</p>
            </div>
            
            <div className="space-y-2">
              <p className="text-slate-500 text-xs uppercase font-bold tracking-wider">Status</p>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${media.isShared ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${media.isShared ? 'bg-green-400' : 'bg-slate-400'}`}></span>
                {media.isShared ? 'Compartilhado' : 'Privado'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MediaDetailPage;

